import { Link } from 'react-router-dom';
import type { GrahaBody, VargaChart } from '@astrally/shared';
import { RASHI_NAMES } from '@astrally/shared';
import { useI18n } from '../../i18n';
import { KUNDLI_GUIDE_ANCHOR } from './ChartStyleGuide';
import {
  ExplainChip,
  HouseHitArea,
  KundliPopover,
  useKundliTouch,
  type HouseTapInfo,
} from './KundliTouch';

/**
 * South Indian (square-grid) chart — the standard format across Tamil Nadu,
 * Kerala, Karnataka, Andhra Pradesh and Telangana.
 *
 * Unlike the North Indian diamond, the SIGNS are fixed in position and the
 * lagna moves: the outer twelve cells of a 4×4 grid always hold the same
 * rashis, running CLOCKWISE from Pisces in the top-left corner. The lagna
 * cell is marked (here with a corner diagonal, the conventional notation)
 * rather than rotating the whole chart.
 *
 * Fixed layout, clockwise from top-left:
 *   Pisces  Aries   Taurus  Gemini
 *   Aquarius  ·       ·     Cancer
 *   Capricorn ·       ·     Leo
 *   Sagittarius Scorpio Libra Virgo
 *
 * The blank 2×2 centre traditionally carries the chart's name.
 */

const PLANET_ABBR: Record<string, string> = {
  sun: 'Su',
  moon: 'Mo',
  mars: 'Ma',
  mercury: 'Me',
  jupiter: 'Ju',
  venus: 'Ve',
  saturn: 'Sa',
  rahu: 'Ra',
  ketu: 'Ke',
  uranus: 'Ur',
  neptune: 'Ne',
  pluto: 'Pl',
  lagna: 'La',
};

/** sign index (0 = Aries) → [column, row] in the 4×4 grid. */
const SIGN_CELL: Record<number, [number, number]> = {
  11: [0, 0], // Pisces
  0: [1, 0], // Aries
  1: [2, 0], // Taurus
  2: [3, 0], // Gemini
  3: [3, 1], // Cancer
  4: [3, 2], // Leo
  5: [3, 3], // Virgo
  6: [2, 3], // Libra
  7: [1, 3], // Scorpio
  8: [0, 3], // Sagittarius
  9: [0, 2], // Capricorn
  10: [0, 1], // Aquarius
};

const SIZE = 300;
const CELL = SIZE / 4;

export function SouthIndianChart({ varga, title }: { varga: VargaChart; title?: string }) {
  const { t } = useI18n();
  const touch = useKundliTouch();
  const lagna = varga.placements.find((p) => p.body === 'lagna');

  const bySign = new Map<number, GrahaBody[]>();
  for (const p of varga.placements) {
    if (p.body === 'lagna') continue;
    if (['uranus', 'neptune', 'pluto'].includes(p.body)) continue;
    const list = bySign.get(p.sign) ?? [];
    list.push(p.body as GrahaBody);
    bySign.set(p.sign, list);
  }

  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="spread" style={{ marginBottom: 8 }}>
        {title ? <div className="h3">{title}</div> : <span aria-hidden />}
        <ExplainChip on={touch.explain} onToggle={touch.toggleExplain} />
      </div>
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ width: '100%', height: 'auto' }}>
        <rect
          x="1"
          y="1"
          width={SIZE - 2}
          height={SIZE - 2}
          fill="none"
          stroke="var(--border-strong)"
          strokeWidth="1.5"
          rx="6"
        />

        {Object.entries(SIGN_CELL).map(([signStr, [col, row]]) => {
          const sign = Number(signStr);
          const x = col * CELL;
          const y = row * CELL;
          const isLagna = lagna?.sign === sign;
          const planets = bySign.get(sign) ?? [];

          return (
            <g key={sign}>
              <rect
                x={x}
                y={y}
                width={CELL}
                height={CELL}
                fill={isLagna ? 'var(--gold-soft)' : 'transparent'}
                stroke="var(--border-strong)"
                strokeWidth="1"
              />
              {/* Conventional lagna notation: a diagonal across the corner. */}
              {isLagna && (
                <line
                  x1={x}
                  y1={y}
                  x2={x + CELL * 0.34}
                  y2={y + CELL * 0.34}
                  stroke="var(--gold)"
                  strokeWidth="1.6"
                />
              )}
              <text
                x={x + CELL - 5}
                y={y + 12}
                textAnchor="end"
                fontSize="9"
                fill="var(--text-faint)"
              >
                {RASHI_NAMES[sign].slice(0, 4)}
              </text>
              {planets.map((body, i) => (
                <text
                  key={body}
                  x={x + 8}
                  y={y + 30 + i * 14}
                  fontSize="12"
                  fontWeight="600"
                  fill={isLagna ? 'var(--gold-ink)' : 'var(--text)'}
                >
                  {PLANET_ABBR[body]}
                </text>
              ))}
              {/* The bhava this fixed rashi cell is, counted from the Lagna —
                  the visible house denotation the Western wheel has always
                  had. Bottom corner, clear of the rashi label (top) and the
                  graha column (left); the legend under the chart explains it.
                  No Lagna, no count — window-mode charts skip it honestly. */}
              {lagna && (
                <text
                  x={x + CELL - 5}
                  y={y + CELL - 6}
                  textAnchor="end"
                  fontSize="9"
                  fill="var(--text-faint)"
                >
                  {((sign - lagna.sign + 12) % 12) + 1}
                </text>
              )}
            </g>
          );
        })}

        {/* Blank 2×2 centre — traditionally the chart's label. */}
        <text
          x={SIZE / 2}
          y={SIZE / 2 - 2}
          textAnchor="middle"
          fontSize="16"
          fontWeight="700"
          fill="var(--text-faint)"
        >
          {varga.varga}
        </text>
        <text
          x={SIZE / 2}
          y={SIZE / 2 + 16}
          textAnchor="middle"
          fontSize="9"
          fill="var(--text-faint)"
        >
          South Indian
        </text>

        {/* Hit areas last, so they sit on top of every painted layer. Signs
            are fixed here, so bhava numbers exist only when the lagna does —
            without one the tap honestly offers the rashi and its grahas. */}
        {Object.entries(SIGN_CELL).map(([signStr, [col, row]]) => {
          const sign = Number(signStr);
          const info: HouseTapInfo = {
            bhava: lagna ? ((sign - lagna.sign + 12) % 12) + 1 : null,
            sign,
            grahas: bySign.get(sign) ?? [],
          };
          return (
            <HouseHitArea
              key={`hit-${sign}`}
              shape={{ kind: 'rect', x: col * CELL, y: row * CELL, width: CELL, height: CELL }}
              info={info}
              explain={touch.explain}
              active={touch.active?.info.sign === sign}
              onTap={touch.tap}
            />
          );
        })}
      </svg>
      <div className="tiny faint center" style={{ marginTop: 6 }}>
        {varga.varga} — {varga.description}
      </div>
      <div className="tiny faint center" style={{ marginTop: 4 }}>
        {lagna ? t('kundli.legend.fixed') : t('kundli.legend.noLagna')}
      </div>
      <div className="center" style={{ marginTop: 6 }}>
        <Link to={`/charts#${KUNDLI_GUIDE_ANCHOR}`} className="tiny accent">
          {t('kundli.howToRead')} →
        </Link>
      </div>
      {touch.active && (
        <KundliPopover info={touch.active.info} anchor={touch.active.anchor} onClose={touch.close} />
      )}
    </div>
  );
}
